import { useMutation, useQuery } from "@tanstack/react-query";
import { queryClient } from "../queryClient";
import { nativeInvoiceContract } from "@common/contracts/native-invoice.contract";
import {
    NativeInvoiceDraftBody,
    NativeInvoiceResendBody,
    NativeInvoiceScheduleBody,
    NativeInvoiceSendBody,
    NativeInvoiceTestSendBody,
    NativeInvoiceUpdateBody,
} from "@common/schemas/domains/invoice/native-invoice.schema";
import { getBackendAPIClient } from "@lib/backendAPIClient";
import { saveBlobResponse } from "@lib/fileDownload";
import { invalidateInvoiceQueries } from "./invoice.api";
import { invalidateTransactionsQueries } from "@components/transactions/useTransactionsQuery";

const nativeInvoiceContractClient = getBackendAPIClient(
    nativeInvoiceContract,
    undefined,
    {
        jsonQuery: true,
    },
);
const nativeInvoiceBlobContractClient = getBackendAPIClient(
    nativeInvoiceContract,
    "blob",
    {
        jsonQuery: true,
    },
);

const NATIVE_INVOICE_PREVIEW_QUERY_ROOT = "native-invoice-preview";

// The drawer hydrates its form from the preview response once per session, so
// every write to a native invoice has to drop the cached preview. Otherwise
// reopening the invoice seeds the form with pre-save content and the next save
// writes that stale snapshot back over the edit.
function invalidateNativeInvoiceQueries() {
    invalidateInvoiceQueries();
    queryClient.invalidateQueries({
        queryKey: [NATIVE_INVOICE_PREVIEW_QUERY_ROOT],
    });
}

export function useCreateNativeInvoice() {
    return useMutation({
        mutationFn: async ({
            workspaceId,
            entityId,
            body,
        }: {
            workspaceId: string;
            entityId: number;
            body: NativeInvoiceDraftBody;
        }) => {
            const result =
                await nativeInvoiceContractClient.createNativeInvoice({
                    params: { workspaceId, entityId },
                    body,
                });
            return result.body;
        },
        onSuccess: () => {
            invalidateNativeInvoiceQueries();
            void invalidateTransactionsQueries();
        },
    });
}

export function useUpdateNativeInvoice() {
    return useMutation({
        mutationFn: async ({
            workspaceId,
            entityId,
            invoiceId,
            body,
        }: {
            workspaceId: string;
            entityId: number;
            invoiceId: string;
            body: NativeInvoiceUpdateBody;
        }) => {
            const result =
                await nativeInvoiceContractClient.updateNativeInvoice({
                    params: { workspaceId, entityId, invoiceId },
                    body,
                });
            return result.body;
        },
        onSuccess: () => {
            invalidateNativeInvoiceQueries();
        },
    });
}

export function useSendNativeInvoice() {
    return useMutation({
        mutationFn: async ({
            workspaceId,
            entityId,
            invoiceId,
            body,
        }: {
            workspaceId: string;
            entityId: number;
            invoiceId: string;
            body: NativeInvoiceSendBody;
        }) => {
            const result = await nativeInvoiceContractClient.sendNativeInvoice({
                params: { workspaceId, entityId, invoiceId },
                body,
            });
            return result.body;
        },
        onSuccess: () => {
            invalidateNativeInvoiceQueries();
        },
    });
}

export function useTestSendNativeInvoice() {
    return useMutation({
        mutationFn: async ({
            workspaceId,
            entityId,
            invoiceId,
            to,
        }: {
            workspaceId: string;
            entityId: number;
            invoiceId: string;
            // Optional recipient override - defaults to the actor's own email
            // server-side when omitted (see nativeInvoiceTestSendBodySchema).
            to?: NativeInvoiceTestSendBody["to"];
        }) => {
            const result =
                await nativeInvoiceContractClient.testSendNativeInvoice({
                    params: { workspaceId, entityId, invoiceId },
                    body: to ? { to } : {},
                });
            return result.body;
        },
    });
}

export function useResendNativeInvoice() {
    return useMutation({
        mutationFn: async ({
            workspaceId,
            entityId,
            invoiceId,
            body,
        }: {
            workspaceId: string;
            entityId: number;
            invoiceId: string;
            body: NativeInvoiceResendBody;
        }) => {
            const result =
                await nativeInvoiceContractClient.resendNativeInvoice({
                    params: { workspaceId, entityId, invoiceId },
                    body,
                });
            return result.body;
        },
        onSuccess: () => {
            invalidateNativeInvoiceQueries();
        },
    });
}

export function useScheduleNativeInvoice() {
    return useMutation({
        mutationFn: async ({
            workspaceId,
            entityId,
            invoiceId,
            body,
        }: {
            workspaceId: string;
            entityId: number;
            invoiceId: string;
            body: NativeInvoiceScheduleBody;
        }) => {
            const result =
                await nativeInvoiceContractClient.scheduleNativeInvoice({
                    params: { workspaceId, entityId, invoiceId },
                    body,
                });
            return result.body;
        },
        onSuccess: () => {
            invalidateNativeInvoiceQueries();
        },
    });
}

export function useCreateNativeInvoiceLogoUploadUrl() {
    return useMutation({
        mutationFn: async ({
            workspaceId,
            entityId,
            name,
        }: {
            workspaceId: string;
            entityId: number;
            name: string;
        }) => {
            const result =
                await nativeInvoiceContractClient.createNativeInvoiceLogoUploadUrl(
                    {
                        params: { workspaceId, entityId },
                        body: { name },
                    },
                );
            return result.body;
        },
    });
}

export function useDownloadNativeInvoicePdf() {
    return useMutation({
        mutationFn: async ({
            workspaceId,
            entityId,
            invoiceId,
            fileName,
        }: {
            workspaceId: string;
            entityId: number;
            invoiceId: string;
            fileName: string;
        }) => {
            const response =
                await nativeInvoiceBlobContractClient.downloadNativeInvoicePdf({
                    params: { workspaceId, entityId, invoiceId },
                });
            saveBlobResponse(response, fileName);
        },
    });
}

export function useDuplicateNativeInvoice() {
    return useMutation({
        mutationFn: async ({
            workspaceId,
            entityId,
            invoiceId,
        }: {
            workspaceId: string;
            entityId: number;
            invoiceId: string;
        }) => {
            const result =
                await nativeInvoiceContractClient.duplicateNativeInvoice({
                    params: { workspaceId, entityId, invoiceId },
                    body: {},
                });
            return result.body;
        },
        onSuccess: () => {
            invalidateNativeInvoiceQueries();
        },
    });
}

export function usePreviewNativeInvoice({
    workspaceId,
    entityId,
    invoiceId,
    enabled = true,
}: {
    workspaceId: string | undefined;
    entityId: number | undefined;
    invoiceId: string | undefined;
    enabled?: boolean;
}) {
    return useQuery({
        queryKey: [
            NATIVE_INVOICE_PREVIEW_QUERY_ROOT,
            workspaceId,
            entityId,
            invoiceId,
        ],
        queryFn: async () => {
            if (!workspaceId || entityId === undefined || !invoiceId) {
                return null;
            }
            const result =
                await nativeInvoiceContractClient.previewNativeInvoice({
                    params: { workspaceId, entityId, invoiceId },
                });
            return result.body;
        },
        enabled:
            enabled &&
            Boolean(workspaceId) &&
            entityId !== undefined &&
            Boolean(invoiceId),
        staleTime: 0,
    });
}
